import { useRef, useCallback } from 'react';

import debounce from 'lodash.debounce';

const mutationObserverConfig = {
  childList: true,
  attributes: true,
  characterData: false,
  subtree: true,
  attributeFilter: ['class'],
  attributeOldValue: false,
  characterDataOldValue: false,
};

type HeightObserverHook = {
  /**
   * Create a height mutation observer for the given DOM node.
   * Return a clean up function used to disconnect the observer.
   */
  observeHeight: (
    dom: HTMLElement,
    onHeightChange: (height: number) => void,
  ) => () => void;
};

const useHeightObserver = (): HeightObserverHook => {
  const previousHeight = useRef<number | null>(null);

  const observeHeight = useCallback(
    (dom: HTMLElement, onHeightChange: (height: number) => void) => {
      if (!('MutationObserver' in window)) {
        return () => {
          // No-op if MutationObserver is not supported
        };
      }

      const updateHeight = () => {
        // Per MDN: scrollHeight is equal to "the minimum height the element
        // would require in order to fit all the content in the viewport
        // without using a vertical scrollbar ... it includes the element's
        // padding, but not its border, margin or horizontal scrollbar."
        //
        // But in testing, it seems like scrollHeight, offsetHeight, and
        // clientHeight are always the same when applied to the body element.
        const height = dom.scrollHeight;

        if (height !== previousHeight.current) {
          previousHeight.current = height;
          onHeightChange(height);
        }
      };

      const observer = new MutationObserver(updateHeight);

      observer.observe(dom, mutationObserverConfig);

      const debounced = debounce(
        () => {
          updateHeight();
        },
        50,
        { maxWait: 200 },
      );
      window.addEventListener('resize', debounced);

      // Dispatch the initial height.
      if (!previousHeight.current) {
        updateHeight();
      }

      return () => {
        observer.disconnect();
        window.removeEventListener('resize', debounced);
      };
    },
    [previousHeight],
  );

  return { observeHeight };
};

export default useHeightObserver;
